/**
 * Conversation Modals — All dialogs triggered from the chat sidebar ellipsis menu
 *
 * PR-AAU-D4 (2026-05-12) — every action is now wired to the backend:
 *
 *   - Rename       PATCH  /conversations/:id { title }
 *   - Delete       DELETE /conversations/:id
 *   - Share        POST   /conversations/:id/share  (chat-sharing.ts)
 *   - Add Project  PUT    /conversations/:id/project { projectId }
 *   - Clone        POST   /conversations/:id/clone
 *   - Archive      PATCH  /conversations/:id { archived: true }
 *   - Download     GET    /conversations/:id/export?format=json|md
 *   - (Pin/Unpin lives inline on the menu row — see conversation-sidebar.tsx)
 *
 * The `/conversations` query is invalidated after every mutation so the
 * sidebar list stays in sync without a manual refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  Copy, Check, Lock, Globe, Users,
  FileText, FileJson, FileCode, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ── Types ───────────────────────────────────────────────────────────────────

export type ModalType =
  | 'rename'
  | 'delete'
  | 'share'
  | 'add-project'
  | 'clone'
  | 'archive'
  | 'download'
  | null;

export interface ConversationModalProps {
  modal: ModalType;
  onClose: () => void;
  conversationTitle: string;
  conversationId: string;
}

interface ProjectDTO {
  id: string;
  name: string;
  color: string;
}

// ── Main Router ─────────────────────────────────────────────────────────────

export function ConversationModals({ modal, onClose, conversationTitle, conversationId }: ConversationModalProps) {
  if (!modal) return null;

  // Bail if we have no id — the menu sets a transient id before opening
  // for newly-created conversations; we don't try to wire those.
  const hasRealId =
    conversationId.length > 0 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(conversationId);

  if (!hasRealId) {
    return (
      <Dialog open={modal !== null} onOpenChange={onClose}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Unavailable</DialogTitle>
            <DialogDescription>This conversation has not been saved yet. Send a message first, then retry.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <RenameModal open={modal === 'rename'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <DeleteModal open={modal === 'delete'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <ShareModal open={modal === 'share'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <AddProjectModal open={modal === 'add-project'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <CloneModal open={modal === 'clone'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <ArchiveModal open={modal === 'archive'} onClose={onClose} title={conversationTitle} id={conversationId} />
      <DownloadModal open={modal === 'download'} onClose={onClose} title={conversationTitle} id={conversationId} />
    </>
  );
}

// ── 1. Rename ───────────────────────────────────────────────────────────────

function RenameModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const [name, setName] = useState(title);
  const qc = useQueryClient();

  useEffect(() => {
    if (open) setName(title);
  }, [open, title]);

  const mutation = useMutation({
    mutationFn: (newTitle: string) =>
      apiClient.patch(`/conversations/${id}`, { title: newTitle }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation renamed');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Rename failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename conversation</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Input
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="Conversation title"
            autoFocus
            disabled={mutation.isPending}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate(name.trim())}
            disabled={!name.trim() || name.trim() === title || mutation.isPending}
          >
            {mutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Saving…</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 2. Delete ───────────────────────────────────────────────────────────────

function DeleteModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiClient.delete(`/conversations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation deleted');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Delete failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete conversation</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "<strong>{title}</strong>"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Deleting…</> : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 3. Share ────────────────────────────────────────────────────────────────

function ShareModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const [copied, setCopied] = useState(false);
  const [visibility, setVisibility] = useState<'private' | 'team' | 'public'>('private');
  const [expiration, setExpiration] = useState('7d');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Generate the share link on open. The backend route returns
  // { shareToken, shareUrl } and is idempotent enough that re-opening
  // the modal just creates a new active share link.
  useEffect(() => {
    if (!open) {
      setShareUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void apiClient
      .post<{ shareToken: string; shareUrl: string }>(`/conversations/${id}/share`, {})
      .then((res) => {
        if (cancelled) return;
        setShareUrl(res.shareUrl);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to create share link');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, id]);

  const handleCopy = () => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Share link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share conversation</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Visibility — UI placeholder; only same-tenant 'team' visibility
              is currently enforced by the backend. */}
          <div className="space-y-2">
            <Label>Who can access</Label>
            <Select value={visibility} onValueChange={(v: string) => setVisibility(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private"><div className="flex items-center gap-2"><Lock className="h-4 w-4" /> Only me</div></SelectItem>
                <SelectItem value="team"><div className="flex items-center gap-2"><Users className="h-4 w-4" /> My team</div></SelectItem>
                <SelectItem value="public"><div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Anyone with link</div></SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Share link */}
          <div className="space-y-2">
            <Label>Share link</Label>
            <div className="flex gap-2">
              <Input
                value={loading ? 'Generating…' : shareUrl ?? error ?? ''}
                readOnly
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={handleCopy}
                aria-label="Copy share link"
                title="Copy"
                disabled={!shareUrl || loading}
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          {/* Expiration — UI placeholder; backend always honors org TTL */}
          <div className="space-y-2">
            <Label>Link expires</Label>
            <Select value={expiration} onValueChange={setExpiration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 hours</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="30d">30 days</SelectItem>
                <SelectItem value="never">Never</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 4. Add to Project ───────────────────────────────────────────────────────

interface AiProjectsResponse {
  projects: Array<{ id: string; name: string; color: string }>;
}

function AddProjectModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: projects, isLoading, isError } = useQuery<AiProjectsResponse>({
    queryKey: ['ai-projects'],
    queryFn: () => apiClient.get('/ai-projects'),
    enabled: open,
  });
  const projectList: ProjectDTO[] = projects?.projects ?? [];

  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const mutation = useMutation({
    mutationFn: (projectId: string) =>
      apiClient.put(`/conversations/${id}/project`, { projectId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['ai-projects'] });
      toast.success('Added to project');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to add to project'),
  });

  // Projects-not-configured fallback: if the /ai-projects endpoint
  // 404s entirely or returns an empty list with an error, fall back
  // to the parking-lot toast described in the PR scope.
  if (isError) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to project</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground py-4">Projects are not configured for this tenant.</p>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add to project</DialogTitle>
          <DialogDescription>Add "{title}" to a project</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 py-2">
          {isLoading ? (
            <p className="text-xs text-muted-foreground px-3 py-4 text-center">Loading projects…</p>
          ) : projectList.length === 0 ? (
            <p className="text-xs text-muted-foreground px-3 py-4 text-center">No projects yet. Create one from the sidebar.</p>
          ) : projectList.map(project => (
            <button
              key={project.id}
              onClick={() => setSelected(project.id)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                selected === project.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              )}
            >
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color ?? '#8b5cf6' }} />
              {project.name}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => selected && mutation.mutate(selected)}
            disabled={!selected || projectList.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Adding…</> : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 6. Clone ────────────────────────────────────────────────────────────────

interface CloneResponse {
  id: string;
  title: string;
}

function CloneModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const [name, setName] = useState(`${title} (copy)`);
  const [includeMessages, setIncludeMessages] = useState(true);
  const qc = useQueryClient();

  useEffect(() => {
    if (open) {
      setName(`${title} (copy)`);
      setIncludeMessages(true);
    }
  }, [open, title]);

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post<CloneResponse>(`/conversations/${id}/clone`, {
        title: name.trim(),
        includeMessages,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation cloned');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Clone failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Clone conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>New title</Label>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Include messages</Label>
              <p className="text-[11px] text-muted-foreground">Copy all messages to the new conversation</p>
            </div>
            <Switch checked={includeMessages} onCheckedChange={setIncludeMessages} disabled={mutation.isPending} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || mutation.isPending}
          >
            {mutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Cloning…</> : 'Clone'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 7. Archive ──────────────────────────────────────────────────────────────

function ArchiveModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.patch(`/conversations/${id}`, { archived: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation archived');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Archive failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Archive conversation</DialogTitle>
          <DialogDescription>
            Archive "<strong>{title}</strong>"? You can restore it later from the archived conversations view.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Archiving…</> : 'Archive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── 8. Download ─────────────────────────────────────────────────────────────

// json + md are real server-side exports. pdf / docx fall back to JSON
// for now (the server-side renderer isn't built yet). When the
// renderer lands, swap `serverFormat` for the real format string.
const DOWNLOAD_FORMATS: Array<{
  id: 'json' | 'md' | 'pdf' | 'docx';
  serverFormat: 'json' | 'md';
  label: string;
  description: string;
  icon: typeof FileText;
  ext: string;
  mime: string;
}> = [
  { id: 'md', serverFormat: 'md', label: 'Markdown', description: 'Plain text with formatting', icon: FileCode, ext: 'md', mime: 'text/markdown' },
  { id: 'json', serverFormat: 'json', label: 'JSON', description: 'Full metadata and structure', icon: FileJson, ext: 'json', mime: 'application/json' },
  // bug #25 — PDF/DOCX rendering is not implemented; the old entries silently
  // downloaded a JSON file mislabeled as the chosen format. Surfacing only the
  // formats that actually produce that file is the honest behavior (truth over
  // speed). Re-add PDF/DOCX here once a real renderer lands server-side.
];

function DownloadModal({ open, onClose, title, id }: { open: boolean; onClose: () => void; title: string; id: string }) {
  const [format, setFormat] = useState<'json' | 'md' | 'pdf' | 'docx'>('md');
  const [busy, setBusy] = useState(false);

  const trigger = async () => {
    const fmt = DOWNLOAD_FORMATS.find((f) => f.id === format) ?? DOWNLOAD_FORMATS[0];
    setBusy(true);
    try {
      // #1083 — issue an authenticated raw fetch DIRECTLY rather than
      // routing through `apiClient.requestRaw`. The shared client's
      // 401 handler calls `onAuthError()` which navigates the entire
      // tab to `/login`. That's the right default for a normal mutation,
      // but a download is an optional side-action: a transient 401
      // (token expired mid-fetch, refresh race, audit-chain stall)
      // must NOT blow the user out of the chat. We instead throw a
      // local error so the modal can show a toast and let the user
      // try again.
      const slug = (title || 'conversation')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'conversation';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `/api/v1/conversations/${id}/export?format=${fmt.serverFormat}`,
        {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) {
        // Specifically callout 401 / 403 so the user knows the next
        // action is to refresh, not "the export is broken". We still
        // do not redirect — the user keeps their chat context.
        if (res.status === 401 || res.status === 403) {
          throw new Error(
            'Your session expired. Refresh the page and try the download again.',
          );
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message ?? `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}.${fmt.ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started');
      onClose();
    } catch (err) {
      toast.error((err as Error).message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Download conversation</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 py-2">
          {DOWNLOAD_FORMATS.map(fmt => {
            const Icon = fmt.icon;
            return (
              <button
                key={fmt.id}
                onClick={() => setFormat(fmt.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                  format === fmt.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                )}
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <div className="text-left">
                  <p className="font-medium">{fmt.label}</p>
                  <p className="text-[11px] text-muted-foreground">{fmt.description}</p>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={trigger} disabled={busy}>
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />Downloading…</> : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Wave 2B: Bulk Action Hooks ─────────────────────────────────────────────
//
// These hooks are consumed by ConversationBulkActionBar to operate on
// multiple conversations at once. Each hook batches API calls in groups
// of 5 for concurrency control, then invalidates the conversations query.

const BULK_CONCURRENCY = 5;

/**
 * Archive multiple conversations in batches.
 */
export function useBulkArchive() {
  const qc = useQueryClient();
  return useCallback(
    async (ids: string[]) => {
      for (let i = 0; i < ids.length; i += BULK_CONCURRENCY) {
        const batch = ids.slice(i, i + BULK_CONCURRENCY);
        await Promise.all(
          batch.map((id) => apiClient.patch(`/conversations/${id}`, { archived: true })),
        );
      }
      await qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    [qc],
  );
}

/**
 * Delete multiple conversations in batches.
 */
export function useBulkDelete() {
  const qc = useQueryClient();
  return useCallback(
    async (ids: string[]) => {
      for (let i = 0; i < ids.length; i += BULK_CONCURRENCY) {
        const batch = ids.slice(i, i + BULK_CONCURRENCY);
        await Promise.all(batch.map((id) => apiClient.delete(`/conversations/${id}`)));
      }
      await qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    [qc],
  );
}

/**
 * Export multiple conversations as a JSON download.
 * Reads from the React Query cache to avoid extra network round-trips.
 */
export function useBulkExport() {
  const qc = useQueryClient();
  return useCallback(
    async (ids: string[]) => {
      type CachedPage = { data: Array<{ id: string; title: string; updatedAt: string; messageCount: number }> };
      const cached = qc.getQueryData<CachedPage>(['conversations']);
      const rowMap = new Map<string, object>();
      for (const row of cached?.data ?? []) rowMap.set(row.id, row);

      const payload = ids.map((id) => rowMap.get(id) ?? { id });
      const json = JSON.stringify(
        { exported_at: new Date().toISOString(), conversations: payload },
        null,
        2,
      );
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [qc],
  );
}
